// Datenzugriff der Projekt-Freischaltung und der Begehungsauftraege
// (ERP/CRM-Plan, Abschnitt 7 „Workflow" Schritte 1–2 — Phase D2).
//
// Unrein: hier liegen ausschliesslich Supabase-Aufrufe und das
// snake_case<->camelCase-Mapping (Stil wie freelancerService.ts: async, throw
// bei error, Mapper oben). Die Fachlichkeit — Uebergangsmatrix, Konflikte,
// Formatierung — steht pur und getestet in assignmentLogic.ts und wird von
// hier nur AUFGERUFEN, nicht nachgebaut.
//
// ─── Schreiben ausschliesslich ueber RPCs ───────────────────────────────────
// Anders als in freelancerService.ts gibt es hier KEIN direktes insert/update
// auf den Tabellen. Jede Aenderung laeuft ueber eine SECURITY-DEFINER-RPC aus
// supabase/freelancer_assignments.sql (MIGRATIONS.md Nr. 19):
//
//   grant_project_access        — prueft Status + Pflichtnachweise serverseitig
//   revoke_project_access       — setzt revoked_at und raeumt Angebote auf
//   create_inspection_assignment— prueft die aktive Freischaltung
//   set_assignment_status       — validiert den Uebergang (Plan 3.4, Befund 3.6)
//
// Dazu ab D4 (supabase/freelancer_inspection.sql, MIGRATIONS.md Nr. 21):
//
//   set_assignment_status/3     — dieselbe RPC mit Pflichtkommentar fuer die
//                                 Nachbesserung (Vier-Augen-Pruefung)
//   rate_assignment             — 1-Klick-Bewertung, fuehrt freelancers.rating
//                                 ueber einen Trigger nach
//   link_assignment_report      — Berichtsverknuepfung nachtragen
//
// Grund: „Statusuebergaenge nur ueber RPCs mit Uebergangs-Validierung". Ein
// direkter Client-Schreibweg neben der RPC waere genau das Loch, das die
// Validierung umgeht — auch fuer den Innendienst.

import { supabase } from './supabaseClient';
import { describeError } from './errorMessage';
import {
    AssignmentStatus,
    FreelancerProjectAccess,
    InspectionAssignment,
} from './assignmentLogic';
import { AssignmentRating } from './freelancerInspectionLogic';

// ─── Row-Mapper ──────────────────────────────────────────────────────────────

const mapAccessRow = (row: any): FreelancerProjectAccess => ({
    id: row.id,
    companyId: row.company_id,
    freelancerId: row.freelancer_id,
    projectId: row.project_id,
    grantedBy: row.granted_by,
    grantedAt: row.granted_at,
    revokedAt: row.revoked_at ?? undefined,
});

const mapAssignmentRow = (row: any): InspectionAssignment => ({
    id: row.id,
    companyId: row.company_id,
    freelancerId: row.freelancer_id,
    projectId: row.project_id,
    zeitraumVon: row.zeitraum_von,
    zeitraumBis: row.zeitraum_bis,
    // int aus PostgREST kommt als Zahl, bei numeric-Umwegen aber als String —
    // Number() haelt den Cent-Betrag verlaesslich rechenbar.
    verguetungCents: Number(row.verguetung_cents ?? 0),
    status: row.status,
    reportId: row.report_id ?? undefined,
    createdAt: row.created_at,
});

// ─── Freischaltungen: lesen ──────────────────────────────────────────────────

export interface AccessQuery {
    /** Auch entzogene Zeilen mitliefern (Historie). Standard: nur aktive. */
    inklusiveEntzogene?: boolean;
}

/** Alle Freischaltungen EINES Projekts — die Liste im ProjectHub. */
export const fetchProjectAccess = async (
    projectId: string,
    q: AccessQuery = {},
): Promise<FreelancerProjectAccess[]> => {
    if (!projectId) return [];
    let query = supabase
        .from('freelancer_project_access')
        .select('*')
        .eq('project_id', projectId)
        .order('granted_at', { ascending: false });
    if (!q.inklusiveEntzogene) query = query.is('revoked_at', null);

    const { data, error } = await query;
    if (error) throw new Error(describeError(error));
    return (data || []).map(mapAccessRow);
};

/** Alle Freischaltungen EINES Freelancers — die Liste in der Akte. */
export const fetchFreelancerAccess = async (
    freelancerId: string,
    q: AccessQuery = {},
): Promise<FreelancerProjectAccess[]> => {
    if (!freelancerId) return [];
    let query = supabase
        .from('freelancer_project_access')
        .select('*')
        .eq('freelancer_id', freelancerId)
        .order('granted_at', { ascending: false });
    if (!q.inklusiveEntzogene) query = query.is('revoked_at', null);

    const { data, error } = await query;
    if (error) throw new Error(describeError(error));
    return (data || []).map(mapAccessRow);
};

// ─── Freischaltungen: schreiben (RPC) ────────────────────────────────────────

/**
 * Freischalten. Die Vorbedingung (Status aktiv, gueltige Pflichtnachweise)
 * prueft die RPC SERVERSEITIG nach — der Client prueft dasselbe vorher mit
 * `einsatzbereitschaft`, damit der Knopf gar nicht erst anklickbar ist.
 *
 * Rueckgabe ist die id der AKTIVEN Freischaltung. Ein zweiter Aufruf liefert
 * dieselbe id zurueck (Idempotenz in der RPC), statt am Teilindex zu
 * scheitern.
 */
export const grantProjectAccess = async (
    freelancerId: string,
    projectId: string,
): Promise<string> => {
    const { data, error } = await supabase.rpc('grant_project_access', {
        p_freelancer: freelancerId,
        p_project: projectId,
    });
    if (error) throw new Error(describeError(error));
    return String(data);
};

/**
 * Freischaltung entziehen.
 *
 * Rueckgabe = Zahl der bereits ANGENOMMENEN Auftraege, die dabei stehen
 * bleiben. Sie ist der Grund, warum die RPC ueberhaupt etwas zurueckgibt:
 * ein `raise notice` erreicht den Browser nicht, und der Sachbearbeiter muss
 * erfahren, dass er noch etwas zu klaeren hat.
 */
export const revokeProjectAccess = async (accessId: string): Promise<number> => {
    const { data, error } = await supabase.rpc('revoke_project_access', {
        p_access: accessId,
    });
    if (error) throw new Error(describeError(error));
    return Number(data ?? 0);
};

// ─── Auftraege: lesen ────────────────────────────────────────────────────────

export interface AssignmentQuery {
    projectId?: string;
    freelancerId?: string;
    /** Nur diese Status. Leer/fehlend = alle. */
    status?: AssignmentStatus[];
}

/**
 * Auftraege laden. Mindestens `projectId` oder `freelancerId` angeben — eine
 * ungefilterte Abfrage ueber alle Auftraege aller Projekte beantwortet keine
 * Frage, die in dieser Oberflaeche gestellt wird, und waechst mit der Zeit.
 */
export const fetchAssignments = async (q: AssignmentQuery = {}): Promise<InspectionAssignment[]> => {
    if (!q.projectId && !q.freelancerId) return [];
    let query = supabase
        .from('inspection_assignments')
        .select('*')
        .order('zeitraum_von', { ascending: false });
    if (q.projectId) query = query.eq('project_id', q.projectId);
    if (q.freelancerId) query = query.eq('freelancer_id', q.freelancerId);
    if (q.status?.length) query = query.in('status', q.status);

    const { data, error } = await query;
    if (error) throw new Error(describeError(error));
    return (data || []).map(mapAssignmentRow);
};

/** Ein einzelner Auftrag — nach einem Statuswechsel, um den Stand zu holen. */
export const fetchAssignment = async (id: string): Promise<InspectionAssignment | null> => {
    const { data, error } = await supabase
        .from('inspection_assignments')
        .select('*')
        .eq('id', id)
        .maybeSingle();
    if (error) throw new Error(describeError(error));
    return data ? mapAssignmentRow(data) : null;
};

// ─── Auftraege: schreiben (RPC) ──────────────────────────────────────────────

export interface NewAssignment {
    freelancerId: string;
    projectId: string;
    /** ISO-Datum (YYYY-MM-DD), beide Enden EINGESCHLOSSEN. */
    zeitraumVon: string;
    zeitraumBis: string;
    /** Verguetung in CENT. Vorbelegt aus satz_je_begehung_cents, danach fest. */
    verguetungCents: number;
}

/**
 * Auftrag erteilen. Die RPC prueft die aktive Freischaltung und faengt die
 * Zeitraum-Ueberschneidung ab (EXCLUDE-Constraint) — beides kommt als
 * deutscher Klartext zurueck.
 *
 * Rueckgabe ist der angelegte Auftrag, nicht nur seine id: die aufrufende
 * Liste soll ihn ohne zweite Rundreise anzeigen koennen.
 */
export const createAssignment = async (a: NewAssignment): Promise<InspectionAssignment> => {
    const { data, error } = await supabase.rpc('create_inspection_assignment', {
        p_freelancer: a.freelancerId,
        p_project: a.projectId,
        p_von: a.zeitraumVon,
        p_bis: a.zeitraumBis,
        p_verguetung_cents: a.verguetungCents,
    });
    if (error) throw new Error(describeError(error));

    const id = String(data ?? '');
    const angelegt = id ? await fetchAssignment(id) : null;
    if (!angelegt) {
        throw new Error('Der Auftrag wurde angelegt, liess sich aber nicht laden. Bitte neu laden.');
    }
    return angelegt;
};

/**
 * Statuswechsel. Der EINZIGE Schreibweg auf `status` (Plan 3.4, Befund 3.6);
 * welche Ziele ueberhaupt anklickbar sind, entscheidet
 * assignmentLogic.ASSIGNMENT_TRANSITIONS — die Datenbank entscheidet es
 * verbindlich noch einmal.
 */
export const setAssignmentStatus = async (
    assignmentId: string,
    status: AssignmentStatus,
): Promise<InspectionAssignment | null> => {
    const { error } = await supabase.rpc('set_assignment_status', {
        p_assignment: assignmentId,
        p_new: status,
    });
    if (error) throw new Error(describeError(error));
    return fetchAssignment(assignmentId);
};

/** Storno durch den Innendienst — Kurzform fuer den Knopf an der Zeile. */
export const cancelAssignment = async (
    assignmentId: string,
): Promise<InspectionAssignment | null> => setAssignmentStatus(assignmentId, 'storniert');

// ─── Vier-Augen-Pruefung (D4) ────────────────────────────────────────────────

/**
 * Alle Auftraege, deren Bericht zur Pruefung liegt — mandantenweit.
 *
 * Bewusst OHNE Projekt- oder Freelancer-Filter, anders als `fetchAssignments`:
 * die Pruefliste ist genau die Frage „was liegt UEBERALL offen?". Der Status
 * begrenzt die Menge ausreichend — es gibt nie viele gleichzeitig, und wenn
 * doch, ist genau das die Aussage der Liste.
 */
export const fetchReviewQueue = async (): Promise<InspectionAssignment[]> => {
    const { data, error } = await supabase
        .from('inspection_assignments')
        .select('*')
        .eq('status', 'bericht_eingereicht')
        .order('zeitraum_bis', { ascending: true });
    if (error) throw new Error(describeError(error));
    return (data || []).map(mapAssignmentRow);
};

/**
 * Freigeben oder zur Nachbesserung zurueckgeben.
 *
 * Die dreistellige Fassung von `set_assignment_status`
 * (supabase/freelancer_inspection.sql, Abschnitt 3) nimmt den Pflichtkommentar
 * entgegen und legt ihn im `assignment_status_log` ab — dort liest ihn das
 * Portal des Freelancers wieder aus. Ohne Begruendung weist die RPC eine
 * Nachbesserung ab; der Client prueft dasselbe vorher mit `kommentarFehler`,
 * damit der Knopf gar nicht erst ausloest.
 */
export const reviewAssignment = async (
    assignmentId: string,
    ziel: Extract<AssignmentStatus, 'geprueft' | 'nachbesserung'>,
    kommentar?: string,
): Promise<InspectionAssignment | null> => {
    const { error } = await supabase.rpc('set_assignment_status', {
        p_assignment: assignmentId,
        p_new: ziel,
        p_comment: (kommentar || '').trim() || null,
    });
    if (error) throw new Error(describeError(error));
    return fetchAssignment(assignmentId);
};

/** Die Statushistorie EINES Auftrags — inklusive der Pruefkommentare. */
export interface AssignmentStatusEintrag {
    id: string;
    oldStatus?: AssignmentStatus;
    newStatus: AssignmentStatus;
    userId?: string;
    comment?: string;
    createdAt: string;
}

export const fetchAssignmentStatusLog = async (
    assignmentId: string,
): Promise<AssignmentStatusEintrag[]> => {
    if (!assignmentId) return [];
    const { data, error } = await supabase
        .from('assignment_status_log')
        .select('id, old_status, new_status, user_id, comment, created_at')
        .eq('assignment_id', assignmentId)
        .order('created_at', { ascending: false });
    if (error) throw new Error(describeError(error));
    return (data || []).map((row: any) => ({
        id: String(row.id),
        oldStatus: row.old_status ?? undefined,
        newStatus: row.new_status,
        userId: row.user_id ?? undefined,
        comment: row.comment ?? undefined,
        createdAt: row.created_at,
    }));
};

// ─── Bewertung (D4) ──────────────────────────────────────────────────────────

const mapRatingRow = (row: any): AssignmentRating => ({
    id: String(row.id),
    assignmentId: String(row.assignment_id),
    freelancerId: String(row.freelancer_id),
    qualitaet: Number(row.qualitaet),
    zuverlaessigkeit: Number(row.zuverlaessigkeit),
    kommentar: row.kommentar ?? undefined,
    ratedBy: String(row.rated_by),
    createdAt: row.created_at,
});

/** Bereits vorhandene Bewertungen zu einer Menge von Auftraegen. */
export const fetchAssignmentRatings = async (
    assignmentIds: string[],
): Promise<Map<string, AssignmentRating>> => {
    const eindeutig = Array.from(new Set((assignmentIds || []).filter(Boolean)));
    if (eindeutig.length === 0) return new Map();

    const { data, error } = await supabase
        .from('assignment_ratings')
        .select('*')
        .in('assignment_id', eindeutig);
    if (error) throw new Error(describeError(error));

    const map = new Map<string, AssignmentRating>();
    for (const row of (data || []) as any[]) map.set(String(row.assignment_id), mapRatingRow(row));
    return map;
};

/**
 * Bewertung speichern (Innendienst).
 *
 * Upsert je Auftrag — eine Korrektur ist ein normaler Vorgang, ein zweiter
 * Datensatz zur selben Leistung waere eine Verfaelschung des Durchschnitts.
 * `freelancers.rating` zieht ein Trigger nach; der Client rechnet ihn NICHT
 * selbst aus, sondern laedt den Stammsatz bei Bedarf neu.
 */
export const rateAssignment = async (
    assignmentId: string,
    qualitaet: number,
    zuverlaessigkeit: number,
    kommentar?: string,
): Promise<string> => {
    const { data, error } = await supabase.rpc('rate_assignment', {
        p_assignment: assignmentId,
        p_qualitaet: qualitaet,
        p_zuverlaessigkeit: zuverlaessigkeit,
        p_kommentar: (kommentar || '').trim() || null,
    });
    if (error) throw new Error(describeError(error));
    return String(data ?? '');
};

/**
 * Berichtsverknuepfung nachtragen (Innendienst).
 *
 * Beim Einreichen setzt `set_assignment_status` sie nur, wenn zu diesem
 * Zeitpunkt schon eine Archivzeile im Auftragszeitraum liegt — und die
 * entsteht erst beim Versand. Diese Funktion ist der benannte Weg, sie
 * spaeter zu setzen.
 */
export const linkAssignmentReport = async (
    assignmentId: string,
    reportId: string | null,
): Promise<void> => {
    const { error } = await supabase.rpc('link_assignment_report', {
        p_assignment: assignmentId,
        p_report: reportId,
    });
    if (error) throw new Error(describeError(error));
};

// ─── Projekte als Anzeigebezug ───────────────────────────────────────────────

export interface ProjektKopf {
    id: string;
    name: string;
    projectNumber?: string;
}

/**
 * Projektnamen zu einer Menge von Ids — die Auftragsliste in der
 * Freelancer-Akte zeigt Projekte, kennt aber nur deren uuid.
 *
 * Bewusst schlank selektiert (kein `select('*')`): die vollstaendige
 * Projektzeile traegt Berichte und Fotos im jsonb und waere fuer eine
 * Namensspalte masslos (gleiche Regel wie freelancerService.fetchProjektPunkte).
 */
export const fetchProjektKoepfe = async (ids: string[]): Promise<Map<string, ProjektKopf>> => {
    const eindeutig = Array.from(new Set((ids || []).filter(Boolean)));
    if (eindeutig.length === 0) return new Map();

    const { data, error } = await supabase
        .from('projects')
        .select('id, name, project_number')
        .in('id', eindeutig);
    if (error) throw new Error(describeError(error));

    const map = new Map<string, ProjektKopf>();
    for (const row of (data || []) as any[]) {
        map.set(String(row.id), {
            id: String(row.id),
            name: String(row.name || 'Unbenanntes Projekt'),
            projectNumber: String(row.project_number || '') || undefined,
        });
    }
    return map;
};
